import { DeliveryMethodCodes } from "~/assets/constants/ecomApi";
import { PaymentService } from "~/services/payment.service.js";
import type {
  CustomerAddressItem,
} from "~/types/ecom/address.type";
import type { Branch } from "~/types/ecom/branch.type";
import type { Product } from "~/types/ecom/product/product.type";
import { TrolleyStatus } from "~/types/ecom/trolley/trolley-map";
import type {
  TrolleyDeliveryMethodDetail,
} from "~/types/ecom/trolley/trolley.type";
import { TrolleyChannel } from "~/types/ecom/trolley/trolley-map";
import type { PostNLPointResponse } from "~/types/postnl.type";
import {customClientErrorHandlerAsync} from "~/services-optimized/customErrorService";


export enum PaymentMethod {
  CreditCard = "credit_card",
  Paypal = "paypal",
  GooglePay = "google_pay",
  LocalPay = "local_pay",
  ApplePay = "apple_pay",
}

export type BuyNowTrolleyResource = {
  id: string;
  session_id?: string;
  session_token: string;
  status: TrolleyStatus;
  item: {
    product_code: Product["code"];
    quantity: number;
    channel: TrolleyChannel;
  };
  amount: {
    order_subtotal: string;
    total_vat: string;
    order_total: string;
    total_exc_vat: string;
  };
  final_amount: string;
  order_summary_loading: boolean;
  shipping_address: CustomerAddressItem;
  collection_address: Branch;
};

interface TransactionParams {
  trolley_id: string;
  order_total: number;
  billing_address_id: string;
  payment_type: string;
  payment_method: string;
  payment_nonce: string;
  payment_device_data: string;
  delivery_address_id?: string;
  collection_address_id?: string;
  next_day_collection_address_id?: string;
}

export const useCheckoutStore = defineStore("checkoutStore", {
  state: () => ({
    payment_service: null as PaymentService | null,
    buy_now_trolley: <BuyNowTrolleyResource>{},
    flash_payment_according_bg: false,
    order: {
      order_id: "",
      order_total: "",
      delivery_price: "",
      collection_price: "",
      products: [],
      collection_address_details: "",
      delivery_address_details: "",
    },
    payment_error: {
      gpay_error: "",
      hosted_field_error: "",
      paypal_error: "",
      ideal_error: "",
      applepay_error: "",
    },

    payment_success: "",
    po_number: "",
    clientToken: "",
    selected_ideal_bank: "",
    amount: useTrolleyStore().final_order_total_amount,
    user: useAuthStore().user,
    addresses: "",
    payment_error_message: "",
    device_data: "",
    shipping_cost: 0,
    selected_collection_address: <Branch | null>null,
    selected_delivery_address: <CustomerAddressItem | null>null,

    currently_saved_post_nl_address_id: "",
    post_nl_address_ids: <string[]>[],

    billing_address: <CustomerAddressItem | null>null,
    is_billing_same_as_shipping: false,
    delivery_methods: <TrolleyDeliveryMethodDetail[]>[],
    cost_bearing_delivery_method: <
        TrolleyDeliveryMethodDetail | null | undefined
        >null,
    authorization_key: "",

    steps_completed: [],
    current_step: "address",

    hasOptedForEmailMarketing: false,
    is_branch_modal_visible: false,
    is_buy_now_journey_active: false,
    is_updating: false,
    is_editing_billing_address: false,
    is_saving_branch_as_shipping: false,
    is_ideal_loading: false,
    is_card_button_loading: false,
    is_card_button: false,
    is_order_loading: true,
    is_transaction_loading: false,
    is_apple_pay_supported: false,
    is_address_loading: true,
    ideal_bank_options: [
      {
        name: "Rabobank",
        code: "RABONL2U",
      },
      {
        name: "ABN AMRO",
        code: "ABNANL2A",
      },
      {
        name: "Van Lanschot Bankiers",
        code: "FVLBNL22",
      },
      {
        name: "Triodos Bank",
        code: "TRIONL2U",
      },
      {
        name: "ING Bank",
        code: "INGBNL2A",
      },
      {
        name: "SNS Bank",
        code: "SNSBNL2A",
      },
      {
        name: "ASN",
        code: "ASNBNL21",
      },
      {
        name: "RegioBank",
        code: "RBRBNL21",
      },
      {
        name: "Knab",
        code: "KNABNL2H",
      },
      {
        name: "Bunq",
        code: "BUNQNL2A",
      },
      {
        name: "Moneyou",
        code: "MOYONL21",
      },
      {
        name: "Handelsbanken",
        code: "HANDNL2A",
      },
    ],
    braintree : null,
  }),
  getters: {
    isAddresses(state): boolean {
      const isAllItemsDelivery = useTrolleyStore().trolley.is_all_lines_delivery;
      const isAllItemsCollection = useTrolleyStore().trolley.is_all_lines_collection;
      let isRequiredAddressesPresent = false;
      // check for required address only if certain channel products are found in trolley
      if (isAllItemsDelivery || isAllItemsCollection){
        if(isAllItemsDelivery) isRequiredAddressesPresent = Boolean(state.selected_delivery_address);
        else if(isAllItemsCollection) isRequiredAddressesPresent = Boolean(state.selected_collection_address);
      }
      else {
        // check for both address existence in case of both types of products
        isRequiredAddressesPresent = Boolean(state.selected_delivery_address && state.selected_collection_address);
      }
      if ( !isRequiredAddressesPresent || !state.billing_address?.id || state.is_editing_billing_address ) {
        return false;
      } else return true;
    },
  },
  persist: [
    {
      storage: import.meta.client ? localStorage : undefined,
      pick: ["billing_address", "post_nl_address_ids", "buy_now_trolley", "selected_collection_address"],
    },
    {
      pick: ["buy_now_trolley.id", "is_buy_now_journey_active"]
    }
  ],
  actions: {
    //----------------------- Buy Now Actions-------------------//
    async deleteBuyNowTrolley() {
      const { deleteBuyNowTrolley } = await import('~/stores-datalayer/checkout.datalayer');
      return await deleteBuyNowTrolley();
    },

    async addToBuyNow(
        product_code: Product["code"],
        quantity: number = 1,
        channel: TrolleyChannel = 1,
        delivery_method_code: DeliveryMethodCodes
    ) {
      const { addToBuyNow } = await import('~/stores-datalayer/checkout.datalayer');
      return await addToBuyNow(product_code, quantity, channel, delivery_method_code);
    },
    async addToBuyNowForDelivery(code: Product["code"], quantity: number = 1)
    {
      await this.addToBuyNow(
          code,
          quantity,
          TrolleyChannel.Delivery,
          DeliveryMethodCodes.Delivery
      );
    },
    async addToBuyNowForCollection(
        code: Product["code"],
        quantity: number = 1,
        branch?: Branch
    ) {
      // save the branch if the user chooses buy now for click and collect
      if (branch) {
        await useBranchStore().onSaveBranch(branch);
      }

      await this.addToBuyNow(
          code,
          quantity,
          TrolleyChannel.Collection,
          DeliveryMethodCodes.Collection
      );
    },
    async addToBuyNowForDirectship(
        code: Product["code"],
        quantity: number = 1
    ) {
      await this.addToBuyNow(
          code,
          quantity,
          TrolleyChannel.Directship,
          DeliveryMethodCodes.Directship
      );
    },
    async addToBuyNowForNextDayCollection(
        code: Product["code"],
        quantity: number = 1
    ) {
      await this.addToBuyNow(
          code,
          quantity,
          TrolleyChannel.NextDayCollection,
          DeliveryMethodCodes.NextDayCollection
      );
    },
    /* To set the order total summary with complete breakdown */
    async setBuyNowTrolleyAmount()
    {
      const { setBuyNowTrolleyAmount } = await import('~/stores-datalayer/checkout.datalayer');
      return await setBuyNowTrolleyAmount();
    },
    //----------------------- get payment client token -------------------//
    async initialize(reFetchToken = false) {
      try {
        await this.getPaymentToken(reFetchToken);
        this.selected_ideal_bank = "";
        this.payment_service = new PaymentService();
        this.is_ideal_loading = false;
        // Initialize all payment methods
        this.order = {
          order_id: "",
          order_total: "",
          delivery_price: "",
          collection_price: "",
          products: [],
          collection_address_details: "",
          delivery_address_details: "",
        };
        await this.initializePaymentMethods();
      } catch (error) {
        const {customClientErrorHandler} = await import("~/services-optimized/customErrorService");
        let errorMeta = { checkoutError: true }

        customClientErrorHandler(
            error,
            'critical',
            'Checkout Store Page Error - trying to initialize all payment method based on token using initialize()',
            errorMeta
        );
      }
    },

    async getPaymentToken(reFetchToken = false) {
      if (!this.authorization_key || reFetchToken) {

        let id = '';

        const authStore = useAuthStore();

        if(authStore.guest){
          id = authStore.guest.id;
        }else if(authStore.user){
          id = authStore.user.id;
        }

        if (!id) {
          return {
            success: false,
            errors: [
                "Unable to get user id from auth store"
            ]
          };
        }

        let response = await useAjaxEcom(
            `/payments/client-token/${id}`
        );

        if (response && response.data && response.data.token) {
          this.authorization_key = response.data.token;
          return {
            success: true
          };
        }
      }
    },

    //----------------initialize different payment methods-----------------//
    async initializePaymentMethods() {
      try {
        await this.initializePaymentMethod(PaymentMethod.CreditCard);
        await this.initializePaymentMethod(PaymentMethod.LocalPay);
        await this.initializePaymentMethod(PaymentMethod.GooglePay);
        await this.initializePaymentMethod(PaymentMethod.Paypal);
        // await this.initializePaymentMethod(PaymentMethod.ApplePay);
      } catch (error) {
        const {customClientErrorHandler} = await import("~/services-optimized/customErrorService");
        let errorMeta = {methodOfPayment: PaymentMethod, checkoutError: true}

        customClientErrorHandler(
             error,
            'high',
            'Checkout Store Page Error - trying to initialize Payment Methods using initializePaymentMethods()',
            errorMeta
        );
      }
    },

    async initializePaymentMethod(method: PaymentMethod,trolleyDetail = null) {
      let errorTitle;
      let error;

      try {
        if (!this.payment_service) {
          errorTitle = "PaymentService is not initialized.";
          error = new Error(errorTitle);
          await customClientErrorHandlerAsync(
            error,
            'critical',
            'Checkout Store Page Error - trying to initialize Payment Methods using initializePaymentMethod()',
              {
                methodOfPayment: method
              }
          )
          throw new Error("PaymentService is not initialized.");
        }

        await this.payment_service.initializeBraintree(this.authorization_key);
        await this.payment_service.setPaymentMethod(method);
        await this.payment_service.initializePayment(trolleyDetail);
      } catch (error)
      {
        const {customClientErrorHandler} = await import("~/services-optimized/customErrorService");
        let errorMeta = {
          method,
          service: this.payment_service, 
          checkoutError: true
        }

        customClientErrorHandler(
             error,
            'high',
            'Checkout Store Page Error - trying to initialize Payment Methods using initializePaymentMethod()',
            errorMeta
        );

        throw error;

      }
    },

    async processPayment() {
      try {
        if (!this.payment_service) {
          throw new Error("PaymentService is not initialized.");
        }
        await this.payment_service.processPayment();
        this.payment_success = "Payment processed successfully.";
      } catch (error) {
        const {customClientErrorHandler} = await import("~/services-optimized/customErrorService");
        let errorMeta = { service: this.payment_service, checkoutError: true}

        customClientErrorHandler(
             error,
            'high',
            'Checkout Store Page Error - trying to process Payment using processPayment()',
            errorMeta
        );
      }
    },

    /* billing address setter */
    setBillingAddress(address: CustomerAddressItem | null) {
      this.billing_address = address;
    },

    /* shipping address setter */
    setShippingAddress(address: CustomerAddressItem | null) {
      this.selected_delivery_address = address;
      // set billing address if user selects a different address for delivery and toggle is on
      if (this.is_billing_same_as_shipping) this.setBillingAddress(address);
    },

    /* fetch shipping cost and delivery method details  for subsequent requests from ECOM */
    async getCheckoutDeliveryMethods(options: {
      trolleyId: string,
      trolleySessionToken?: string | null,
      shippingAddress: CustomerAddressItem,
      collectionAddress?: Branch | null,
      isBuyNow?: boolean
    })
    {
      const { getCheckoutDeliveryMethods } = await import('~/stores-datalayer/checkout.datalayer');
      return await getCheckoutDeliveryMethods(options);

    },

    async calculateShippingCost(options: {
                                  trolleySessionToken?: string | null,
                                  trolleyLines: number[],
                                  newDeliveryMethodCode: DeliveryMethodCodes,
                                  isBuyNow?: boolean
                                }
    )
    {
      const { calculateShippingCost } = await import('~/stores-datalayer/checkout.datalayer');
      return await calculateShippingCost(options);
    },

    async handleCheckoutPostNL(savedPoint: PostNLPointResponse)
    {
      const { handleCheckoutPostNL } = await import('~/stores-datalayer/checkout.datalayer');
      return await handleCheckoutPostNL(savedPoint);
    },

    //-------------- create order after successful payment ------------------//
    async createTransaction(nonce: string, type: string, method = 'braintree',trolleyDetail = null)
    {
      const { createTransaction } = await import('~/stores-datalayer/checkout.datalayer');
      return await createTransaction(nonce,type,method,trolleyDetail);
    },

    //-------------- create order after successful payment ------------------//
    async handleProCardPayments() {

      this.createTransaction('__trade-payment-token__',null,'account');
    },

    async buildTransactionParams(nonce: string, type: string, method = 'braintree')
    {
      const { buildTransactionParams } =  await import('~/stores-datalayer/checkout.datalayer');
      return  await buildTransactionParams(nonce,type,method);
    },

    async handleFallback(route: any)
    {
      let errorTitle;

      // initialize payment service when negative conditions are tested
      try {
        this.payment_service = new PaymentService();
        await this.initializePaymentMethod(PaymentMethod.LocalPay);
      }catch (err) {
        errorTitle = "Payment failed: CheckoutStore.initializePaymentMethod threw error";
        await customClientErrorHandlerAsync(
            err,
            'critical',
            errorTitle,
            {
              paymentMethod: PaymentMethod.LocalPay,
              query: route.query,
              paymentError: true,
            }
        );
        throw err;
      }
    },

    resetError() {
      this.payment_error = {
        gpay_error: "",
        hosted_field_error: "",
        paypal_error: "",
        ideal_error: "",
        applepay_error : ""
      };
    },

    flashPaymentAccordingBackground() {
      this.flash_payment_according_bg = true;
      setTimeout(() => {
        this.flash_payment_according_bg = false;
      }, 1000);
    },

    resetPersistAddresses() {
      this.billing_address = null;
      this.post_nl_address_ids = [];
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCheckoutStore, import.meta.hot));
}
